'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Arrow from './Arrow';
import { useAuth } from '@/contexts/AuthContext';
import { supabaseBrowser as supabase } from '@/lib/supabaseBrowser';
import { formatDraw } from '@/lib/homepage/format';

/**
 * For signed-in players: how many live draws they're in, how many tickets,
 * and which draw is next. Shows nothing for visitors or players with no
 * live entries.
 */
export default function YourEntries() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!user?.id) {
      setSummary(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('ticket_purchases')
        .select('quantity, competition_id, competitions!inner(title, status, end_date, draw_date)')
        .eq('user_id', user.id)
        .eq('payment_status', 'captured')
        .eq('competitions.status', 'active');
      if (cancelled || error || !data?.length) return;

      const byComp = new Map();
      data.forEach((row) => {
        const prev = byComp.get(row.competition_id);
        const closes = row.competitions?.end_date || row.competitions?.draw_date;
        byComp.set(row.competition_id, {
          title: row.competitions?.title,
          closes,
          tickets: (prev?.tickets || 0) + Number(row.quantity || 0),
        });
      });
      const comps = [...byComp.values()].filter((c) => new Date(c.closes).getTime() > Date.now());
      if (!comps.length) return;
      comps.sort((a, b) => new Date(a.closes) - new Date(b.closes));
      setSummary({
        draws: comps.length,
        tickets: comps.reduce((sum, c) => sum + c.tickets, 0),
        next: comps[0],
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  if (!summary) return null;

  return (
    <div className="bg-[#141a16] border-b border-white/10">
      <div className="max-w-[1520px] mx-auto px-[var(--gutter)] py-4 flex flex-wrap items-center justify-between gap-x-8 gap-y-2">
        <p className="font-semibold">
          You&apos;re in {summary.draws} {summary.draws === 1 ? 'draw' : 'draws'} with{' '}
          <span className="text-accent-bright tabular-nums">{summary.tickets.toLocaleString('en-GB')}</span>{' '}
          {summary.tickets === 1 ? 'ticket' : 'tickets'}.
          <span className="text-muted-foreground font-medium">
            {' '}
            Next: {summary.next.title}, {formatDraw(summary.next.closes)}.
          </span>
        </p>
        <Link href="/user-dashboard" className="link-more">
          My tickets <Arrow />
        </Link>
      </div>
    </div>
  );
}
